/**
 * Hub kiosk placeholder (10 §4): two standing boards at (0,0,0.6) and
 * (0,0,29.4), 1.2 m wide by 2.2 m tall, blueprint plinth + HUD-register
 * screen face. Real materials are art; this draws the frame and the data.
 */
import { DEFAULT_CAMERA, project, type CameraParams } from './camera.js'
import type { KioskMsg } from '../../shared/protocol.js'
import type { Arena, Team } from '../../sim/types.js'
import { SIM_HZ } from '../../sim/constants.js'

export function drawKiosks(ctx: CanvasRenderingContext2D, arena: Arena, cameraTeam: Team, kiosk: KioskMsg | null, now: number, cam: CameraParams = DEFAULT_CAMERA): void {
  if (arena.camera === 'follow') return // the terminal (spec §4) replaces the wall kiosks
  for (const z of [0.6, arena.length - 0.6]) drawKiosk(ctx, arena, cameraTeam, z, kiosk, now, cam)
}

function drawKiosk(ctx: CanvasRenderingContext2D, arena: Arena, cameraTeam: Team, z: number, kiosk: KioskMsg | null, now: number, cam: CameraParams): void {
  const base = project(0, 0, z, cameraTeam, arena, cam)
  const top = project(0, 2.2, z, cameraTeam, arena, cam)
  if (!base.visible || !top.visible) return
  const w = 1.2 * base.scale
  const h = base.sy - top.sy
  // Plinth + frame (blueprint register: hairlines, dimension ticks).
  ctx.strokeStyle = 'rgba(200,210,220,0.6)'
  ctx.lineWidth = 1
  ctx.strokeRect(base.sx - w / 2, top.sy, w, h)
  ctx.fillStyle = '#1b2128'
  ctx.fillRect(base.sx - w / 2 + 2, top.sy + 2, w - 4, h * 0.72)
  ctx.beginPath(); ctx.moveTo(base.sx - w / 2 - 6, base.sy); ctx.lineTo(base.sx + w / 2 + 6, base.sy); ctx.stroke()
  ctx.font = `${Math.max(6, base.scale * 0.09)}px ui-monospace, monospace`
  ctx.fillStyle = 'rgba(200,210,220,0.5)'
  ctx.fillText('KIOSK-01', base.sx - w / 2, base.sy + 10)
  // Screen face: HUD register (09 brackets).
  const px = Math.max(7, base.scale * 0.11)
  ctx.font = `${px}px ui-monospace, monospace`
  ctx.fillStyle = '#dfe6ee'
  const lines: string[] = []
  if (!kiosk) lines.push('…')
  else {
    const rot = Math.floor(now / 6000) % 2
    if (kiosk.live.length) {
      lines.push('LIVE')
      for (const m of kiosk.live) lines.push(`${m.run ? `RUN ${m.run} · ` : ''}${m.teams[0].join('+') || '-'} vs ${m.teams[1].join('+') || '-'}  ${m.score[0]}-${m.score[1]}  R${m.round}  ${fmt(m.timerTicks)}${m.full ? ' FULL' : ''}`)
      lines.push('[Q] SPECTATE')
    } else if (kiosk.lastResult && rot === 0) {
      const r = kiosk.lastResult
      lines.push('LAST RESULT', r.winner === null ? 'ABANDONED' : `TEAM ${r.winner === 0 ? 'A' : 'B'} — ${r.score[0]}:${r.score[1]}`, r.players.map((p) => p.name).join(' · '))
    } else {
      lines.push('MOST PICKED', ...(kiosk.mostPicked.length ? kiosk.mostPicked.map((p) => `${p.id} ×${p.picks}`) : ['—']))
    }
    lines.push('[R] RUN — 3 bouts + the Warden (W1)')
    lines.push(kiosk.queue.flagged ? `QUEUE ${kiosk.queue.flagged}/4${kiosk.queue.launchInMs !== null ? ` · launching in ${fmt(Math.ceil((kiosk.queue.launchInMs / 1000) * SIM_HZ))}` : ''}` : 'QUEUE 0 — [TAB] TO JOIN')
    lines.push(`ONLINE ${kiosk.online} · IN MATCH ${kiosk.inMatch} · HUB ${kiosk.inHub}`)
    if (kiosk.drain) lines.push(`SERVER RESTARTING IN ${fmt(Math.ceil((kiosk.drain.graceMs / 1000) * SIM_HZ))}`)
  }
  const far = base.scale < 30 // beyond ~6 m: only the strip + timer (10 §4)
  const shown = far ? lines.slice(0, 2) : lines
  let y = top.sy + px + 4
  for (const l of shown) { ctx.fillText(l.slice(0, 40), base.sx - w / 2 + 4, y); y += px + 2 }
  // L-corner brackets.
  ctx.strokeStyle = '#8fd18f'
  const c = 6
  ctx.beginPath(); ctx.moveTo(base.sx - w / 2 + 2, top.sy + 2 + c); ctx.lineTo(base.sx - w / 2 + 2, top.sy + 2); ctx.lineTo(base.sx - w / 2 + 2 + c, top.sy + 2); ctx.stroke()
  ctx.beginPath(); ctx.moveTo(base.sx + w / 2 - 2, top.sy + 2 + h * 0.72 - c); ctx.lineTo(base.sx + w / 2 - 2, top.sy + 2 + h * 0.72); ctx.lineTo(base.sx + w / 2 - 2 - c, top.sy + 2 + h * 0.72); ctx.stroke()
}

const fmt = (ticks: number): string => { const s = Math.max(0, Math.floor(ticks / SIM_HZ)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` }
